'use client';

import { Combobox } from '@headlessui/react';
import { forwardRef, type ForwardedRef, type RefObject } from 'react';
import {
  useController,
  useFormContext,
  type FieldValues,
} from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { useResetPersistedState } from '@/app/lib/hooks/use-reset-persisted-state';
import { ChangeAddressModal } from '@/components/change-address-modal/change-address-modal';
import { CONTACT_SEARCH_ITEM, MODAL_NAMES, NMI_SEARCH_ITEM } from '@/constants';
import { useJoinFormStore } from '@/stores/join-form-store/join-form-store';

import { SearchIcon } from './search-icon';
import { typeaheadVariants } from './style';

import type { DataType, TypeaheadProps } from './typeahead.types';

const TypeaheadBase = <T extends FieldValues>(
  {
    list,
    placeholder,
    name,
    id,
    onChange,
    setAddAddressActive,
    onOptionSelect,
    variant = 'primary',
    disabled = false,
    readOnly = false,
  }: TypeaheadProps<T>,
  ref: RefObject<HTMLInputElement | null>
) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Fix any type
  const { control, setValue } = useFormContext<any>();
  const { field } = useController<T>({ name, control, disabled });
  const {
    property,
    electricityPlanSlug,
    gasPlanSlug,
    gasChecked,
    electricityChecked,
    openModal,
    resetJoinFormStore,
  } = useJoinFormStore();
  const { resetPersistedState } = useResetPersistedState();

  const onClearAddress = () => {
    resetPersistedState();
    resetJoinFormStore();
    setValue('address', { id: '', name: '' });
    if (ref.current) {
      ref.current.value = '';
    }
  };

  const shouldWarnOfAddressReset = () => {
    if (electricityPlanSlug || gasPlanSlug) {
      openModal(MODAL_NAMES.ChangeAddressWarning);
    } else {
      onClearAddress();
    }
  };

  const showReadOnly = Boolean(property.address.full);

  return (
    <Combobox
      as="div"
      {...field}
      onChange={(value: DataType) => {
        if (onOptionSelect) {
          onOptionSelect(value);
        }

        field.onChange(value);
      }}
      onKeyDown={() => {
        if (setAddAddressActive) {
          setAddAddressActive(false);
        }
      }}
    >
      <div className="relative">
        <div className="relative">
          <Combobox.Input
            id={id}
            ref={ref}
            type="search"
            aria-disabled={showReadOnly}
            readOnly={showReadOnly}
            displayValue={(value?: DataType) => value?.name ?? ''}
            className={typeaheadVariants({ variant })}
            placeholder={placeholder}
            onChange={(event) => {
              const query = event.target.value;

              if (query === '') {
                shouldWarnOfAddressReset();
              } else {
                onChange(query);
              }
            }}
          />

          <SearchIcon
            resetInputValue={() => null}
            variant={variant}
            inputValue={ref.current?.value}
            field={field}
            onClick={shouldWarnOfAddressReset}
            onChange={() => null}
            readOnly={readOnly}
            disabled={disabled}
          />
        </div>

        {ref.current?.value && !readOnly ? (
          <div className="relative">
            <Combobox.Options className="max-h-70 border-inset absolute z-10 mt-a w-full overflow-hidden rounded-md border-2 border-pink-600 bg-white text-base shadow sm:text-sm">
              {list.slice(0, 4).map((item) => (
                <Combobox.Option
                  data-testid={`typeahead-${item.id}`}
                  key={item.id}
                  value={item}
                  className={({ active }) =>
                    twMerge(
                      'relative cursor-pointer select-none p-d md:p-c lg:p-b',
                      active && 'bg-gray-50'
                    )
                  }
                >
                  <span className="text-body-2 block truncate">
                    {item.name}
                  </span>
                </Combobox.Option>
              ))}

              <Combobox.Option
                key={NMI_SEARCH_ITEM.id}
                value={
                  gasChecked && !electricityChecked
                    ? CONTACT_SEARCH_ITEM
                    : NMI_SEARCH_ITEM
                }
                className={({ active }) =>
                  twMerge(
                    'relative cursor-pointer select-none bg-white p-d md:p-c lg:p-b',
                    active && list.length > 0 && 'bg-gray-50'
                  )
                }
              >
                <span className="text-body-2 block truncate">
                  {gasChecked && !electricityChecked
                    ? "I can't find my address. Contact Powershop"
                    : "I can't find my address. Search by NMI"}
                </span>
              </Combobox.Option>
            </Combobox.Options>
          </div>
        ) : null}

        <ChangeAddressModal onClearAddress={onClearAddress} />
      </div>
    </Combobox>
  );
};

const TypeaheadWithRef = forwardRef(TypeaheadBase);

export const Typeahead = <T extends FieldValues>({
  inputRef,
  ...rest
}: TypeaheadProps<T> & { inputRef?: ForwardedRef<HTMLInputElement> }) => (
  <TypeaheadWithRef
    {...rest}
    ref={inputRef}
  />
);
